import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment-timezone'
import businessRules from '@/data/businessRules'
import service from '@/data/service'

Vue.use(Vuex)

export default {
  state: {
    //selections
    tab: 0,
    coachId: undefined,
    teamId: undefined,
    seasonId: undefined,
    facilityIds: [],
    slot: undefined,
    space: undefined,

    //data
    dataReady: false,
    coach: {},
    coachOptions: [],
    seasonOptions: [],
    facilityOptions: [],
    registrationGroups: [],
    pendingSessions: [],
    priorityTiming: {},

    lastRefresh: undefined,
    loading: false,
    firstTime: true
  },

  getters: {
    teams(state){
      try {
        return state.coach.teams.filter(team => team.season_id == state.seasonId)
      } catch (err) {
        return []
      }
    },
    team(state, getters) {
      return getters.teams
        .find(team => team.id == state.teamId)
    },
    reservedFacilityIds(state, getters) {
      try {
        let ids = getters.team.reservations.reduce((acc, reservation) => {
          let id = reservation.session.facility_hours.athletic_space.facility.id
          if(!acc.includes(id))
            acc.push(id)
          return acc
        }, []).filter(id => getters.availableFacilities.some(facility => facility.id == id))
        return ids
      } catch (err) {
        return []
      }
    },
    assignedSeasons(state) {
      if('teams' in state.coach)
        return state.seasonOptions.filter(season => {
          return state.coach.teams.some(team => team.season_id == season.id) && season.status.toLowerCase() == 'active'
        })
      return state.seasonOptions
    },
    season(state, getters) {
      return getters.assignedSeasons
        .find(season => season.id == state.seasonId)
    },
    processedFacilityOptions (state, getters) {
      return state.facilityOptions.map(facility => {
        return businessRules.processFacility({
          facility,
          registrationGroup: getters.registrationGroup,
          sport: getters.season.sport
        })
      })
    },
    availableFacilities (state, getters) {
      return getters.processedFacilityOptions
        .filter(facility => facility.IS_AVAILABLE)
    },
    selectedFacilities (state, getters) {
      return getters.availableFacilities.filter(facility => {
        return state.facilityIds.includes(facility.id)
      })
    },
    facilities (state, getters) {
      return getters.selectedFacilities.filter(facility => facility.loaded)
    },
    slotOptions (state, getters) {
      return businessRules.slots({
        facilities: getters.facilities,
        pendingSessions: state.pendingSessions,
        team: getters.team,
        allowanceUsage: getters.allowanceUsage,
        seasonId: state.seasonId
      })
    },
    slotBreakdown (state, getters) {
      return businessRules.slotBreakdown({
        slot: state.slot,
        facilities: getters.facilities,
        pendingSessions: state.pendingSessions,
        team: getters.team,
        seasonId: state.seasonId
      })
    },
    slotString (state, getters) {
      if(state.slot){
        let start = state.slot.start
        let end = state.slot.end
        return getters.slotStringWith({start, end})
      } else
        return null
    },
    slotStringWith: () => ({start, end}, weekday=undefined) => {
      let startDtm = moment(start)
      let endDtm = moment(end)
      if(weekday){
        startDtm.day(weekday)
        endDtm.day(weekday)
      }
      return `${startDtm.format('dddd, h:mmA')}-${endDtm.format('h:mmA')}`
    },
    allowances (state, getters) {
      return businessRules.allowances({
        sport: getters.season.sport,
        type: getters.season.season_type,
        registrationGroup: getters.registrationGroup
      })
    },
    allowanceUsage(state, getters) {
      let usage = Object.entries(getters.allowances).reduce((acc, [key, val]) => {
        acc[key] = new Array(val).fill('free')
        return acc
      }, {})
      let occupySlot = (isWeekend, status) => {
        let target = isWeekend ? 'weekend' : 'weekday'
        let targetIndex = usage[target].findIndex(slot => slot == 'free')
        if(targetIndex == -1) {
          target = 'either'
          targetIndex = usage[target].findIndex(slot => slot == 'free')
        }
        if(targetIndex != -1)
          usage[target][targetIndex] = status
      }
      if(getters.team)
        getters.team.reservations
          .filter(reservation => getters.availableFacilities.some(facility => facility.id == reservation.session.facility_hours.athletic_space.facility.id))
          .forEach(reservation => {
            let isWeekend = ['SATURDAY', 'SUNDAY'].includes(reservation.session.facility_hours.week_day)
            occupySlot(isWeekend, 'reserved')
          })
      state.pendingSessions.forEach(session => {
        let isWeekend = ['Saturday', 'Sunday'].some(day => session.slotString.startsWith(day))
        occupySlot(isWeekend, 'pending')
      })
      return usage
    },
    reservationsAllowedOnCurrentSlot(state, getters) {
      if(!state.slot)
        return false
      let start = moment(state.slot.start)
      let isWeekend = [0, 6].includes(start.day())
      let target = isWeekend ? 'weekend' : 'weekday'
      let potentials = [...getters.allowanceUsage[target], ...getters.allowanceUsage.either]
      return potentials.some(thing => thing == 'free')
    },
    registrationGroup(state, getters) {
      if(!getters.team)
        return null
      let group = state.registrationGroups.find(r => r.division_name == getters.team.division_name)
      if(!group)
        return null
      let isPriority = getters.teams.length >= 2
      let startDate = isPriority ? state.priorityTiming.startDate : group.registration_start_date
      if(group.timing_status == 'TOO_EARLY' && isPriority && !state.priorityTiming.tooEarly)
        group.timing_status = 'ON_TIME'
      let result = {
        ...group,
        isPriority,
        formatted_start_date: moment
          .tz(startDate, 'America/New_York')
          .format('MMM Do H:mma z'),
        formatted_end_date: moment
          .tz(group.registration_end_date, 'America/New_York')
          .subtract(1, 'days')
          .format('MMM Do H:mma z')
      }
      return result
    },
    isInTimingWindow(state, getters) {
      let rg = getters.registrationGroup
      return rg ? rg.timing_status == 'ON_TIME' : rg
    }
  },

  mutations: {
    reset(state) {
      //selections
      state.tab = 0
      state.coachId = null
      state.teamId = null
      state.seasonId = null
      state.facilityIds = []
      state.slot = null
      state.space = null

      //data
      state.dataReady = false
      state.coach = {}
      //state.coachOptions = []
      state.seasonOptions = []
      state.facilityOptions = []
      state.registrationGroups = []
      state.pendingSessions = []
      state.priorityTiming = {}
    },
    setTab(state, value) {
      state.tab = value
    },
    setCoachId(state, value) {
      state.coachId = value
    },
    setCoach(state, value) {
      state.coach = value
    },
    setTeamId(state, value) {
      state.teamId = value
    },
    setCoachOptions(state, value) {
      state.coachOptions = value
    },
    setSeasonOptions(state, value) {
      state.seasonOptions = value
    },
    setSeasonId(state, value) {
      state.seasonId = value
    },
    setFacilityOptions(state, value) {
      state.facilityOptions = value
    },
    resetFacility(state, id) {
      let index = state.facilityOptions.findIndex(f => f.id == id)
      if(index >= 0) {
        let facility = state.facilityOptions[index]
        state.facilityOptions.splice(index, 1, {
          __typename: "facility",
          facility_name: facility.facility_name,
          facility_type: facility.facility_type,
          id: facility.id
        })
      }
    },
    updateFacility(state, {id, value}) {
      let index = state.facilityOptions.findIndex(f => f.id == id)
      if(index >= 0)
        state.facilityOptions.splice(index, 1, value)
    },
    setFacilityIds(state, value) {
      state.facilityIds = value
    },
    setRegistrationGroups(state, value) {
      state.registrationGroups = value
    },
    setSlot(state, value) {
      state.slot = value
    },
    setSpace(state, value) {
      state.space = value
    },
    setPendingSessions(state, value) {
      state.pendingSessions = value
    },
    clearPendingSessions(state) {
      state.pendingSessions = []
    },
    addPendingSession(state, value) {
      state.pendingSessions.push(value)
    },
    removePendingSession(state, id) {
      while(state.pendingSessions.find(session => session.id == id)) {
        let index = state.pendingSessions.findIndex(session => session.id == id)
        state.pendingSessions.splice(index, 1)
      }
    },
    setPriorityTiming(state, value) {
      state.priorityTiming = value
    },
    startLoading(state) {
      state.loading = true
    },
    stopLoading(state) {
      state.loading = false
    },
    clearFirstTime(state) {
      state.firstTime = false
    }
  },
  actions: {
    async initialize ({commit, dispatch}, seasonId = undefined) {
      commit('reset')
      commit('setSeasonId', seasonId)
      await dispatch('refresh')
    },
    async refresh({commit, dispatch, state, getters}) {
      commit('startLoading')
      state.lastRefresh = moment()

      if(state.coachId) {
        let coach = await service.getCoach(state.coachId)
        commit('setCoach', coach)
      }

      let seasonOpts = await service.listSeasons()
      commit('setSeasonOptions', seasonOpts)

      // set to default season if necessary
      if(!getters.assignedSeasons.some(season => season.id == state.seasonId)) {
        try {
          commit('setSeasonId', getters.assignedSeasons.find(season => season.status == 'ACTIVE').id | getters.assignedSeasons[0].id)
        } catch (err) {
          commit('setSeasonId', undefined)
        }
      }

      if(state.seasonId >= 0) {
        if(!state.coachId) { // admin with coach unselected
          let coachOpts = await service.listCoaches(state.seasonId)
          commit('setCoachOptions', coachOpts)
        }

        let season = await service.getSeason(state.seasonId)

        let facilities = season.facilities.sort((a, b) => {
          // sort alphabetically
          let A = a.facility_name.toUpperCase()
          let B = b.facility_name.toUpperCase()
          if (A < B) return -1
          if (B < A) return 1
          return 0
        })
        commit('setFacilityOptions', facilities)
        commit('setRegistrationGroups', season.season_registration_setup)
        commit('setPriorityTiming', {
          startDate: season.priority_start_date,
          tooEarly: season.priority_too_early
        })

        await dispatch('loadFacilities', true)

        // set to default team if necessary
        if(!getters.teams.some(team => team.id == state.teamId)) {
          try {
            await commit('setTeamId', getters.teams[0].id)
            commit('setFacilityIds', getters.reservedFacilityIds)
          } catch (err) {
            commit('setTeamId', undefined)
            commit('setFacilityIds', [])
          }
        }

        await dispatch('checkReservations')
      }

      state.dataReady = true
      commit('stopLoading')
    },
    lazyRefresh({dispatch, state}) {
      let ms = 60 * 1000
      if(!state.lastRefresh || moment().valueOf() - state.lastRefresh.valueOf() >= ms)
        return dispatch('refresh')
      else
        return dispatch('loadFacilities')
    },
    async loadFacilities ({commit, getters, state}, reload=false) {
      let toLoad
      if(reload) {
        // todo clear data on unselected facilities
        getters.availableFacilities
          .filter(f => !state.facilityIds.includes(f.id))
          .forEach(f => commit('resetFacility', f.id))
        toLoad = getters.selectedFacilities
      } else {
        toLoad = getters.selectedFacilities.filter(f => !f.loaded)
      }
      if(!toLoad.length)
        return null
      commit('startLoading')
      for(const facility of toLoad) {
        let facilityData = await service.getFacility(state.seasonId, facility.id)
        facilityData.athletic_space.forEach(a_s => {
          a_s.facility_hours = a_s.facility_hours.filter(f_h => f_h.season_id == state.seasonId)
        })
        facilityData.loaded = true
        commit('updateFacility', {id: facility.id, value: facilityData})
      }
      commit('stopLoading')
    },
    async checkReservations ({state}) {
      for(let pending of state.pendingSessions) {
        let session = await service.getSession(pending.id)
        let slots = session.facility_hours.athletic_space.slots
        let fullReservation = session.reservations.find(r => !r.breakout_id)
        let partialReservations = session.reservations.filter(r => r.breakout_id)
        let slotsRequired = pending.facility.SLOTS_REQUIRED
        pending.valid = slotsRequired == 2 ? !session.reservations.length : !fullReservation && partialReservations.length < slots
      }
    },
    async submitReservations({commit, state, getters, dispatch}) {
      await dispatch('checkReservations')
      let invalidSessions = []
      for(let session of state.pendingSessions) {
        try {
          if(!session.valid)
            throw 'invalid'
          let breakoutId = session.breakout ? session.breakout.id : null
          let success = await service.createReservation(session.id, getters.team.id, breakoutId)
          if(!success)
            throw 'failed to reserve'
        } catch(error) {
          session.valid = false
          invalidSessions.push(session)
        }
      }
      commit('setPendingSessions', invalidSessions)
      return dispatch('refresh')
    },
    async releaseSession ({dispatch, state}, sessionId) {
      await service.deleteReservation(sessionId, state.teamId)
      await dispatch('refresh')
    },
    async selectTeam ({commit, getters, dispatch}, id) {
      commit('startLoading')
      commit('setTeamId', id)
      commit('setFacilityIds', getters.reservedFacilityIds)
      commit('clearPendingSessions')
      await dispatch('lazyRefresh')
      commit('stopLoading')
    },
    async selectSeason ({commit, dispatch}, id) {
      commit('startLoading')
      commit('setSeasonId', id)
      commit('clearPendingSessions')
      await dispatch('refresh')
      commit('stopLoading')
    }
  }
}
